import { apiUrl } from '../config/index.js';
import { ApiError, ApiErrorCode } from './api-error.js';

/**
 * File uploads, with a real progress bar.
 *
 * It lives beside the HTTP client rather than inside a gateway, because one place
 * in this frontend talks to the network and this is that place — the client's file
 * only stopped being the only one because `fetch` cannot do this.
 *
 * **`fetch` does not expose upload progress.** There is no option for it: the body
 * goes out and the promise settles when the answer comes back. `XMLHttpRequest`
 * does, through `xhr.upload`, and that is the entire reason this file uses a
 * fifteen-year-old API on purpose.
 *
 * Two destinations, and the difference between them matters:
 *
 * - `uploadToApi` goes to our own backend, carries the session cookie and unwraps
 *   the envelope. For small things.
 * - `uploadToStorage` goes **straight to the storage** with a pre-signed URL. The
 *   byte never passes through the back-office, which is what makes a video upload
 *   affordable. It sends **no credentials at all**: that host is not ours, and
 *   attaching the session cookie to a request aimed at it would hand it over.
 */

export interface UploadProgress {
  loaded: number;
  total: number;
  /** 0 to 100, already rounded. `null` while the total size is unknown. */
  percent: number | null;
}

export interface UploadOptions {
  onProgress?: (progress: UploadProgress) => void;
  /** Cancels the upload. Leaving the screen mid-upload is the common case. */
  signal?: AbortSignal;
}

interface Envelope<T> {
  traceId: string;
  data: T;
  meta?: Record<string, unknown>;
}

interface ErrorEnvelope {
  traceId?: string;
  error?: { code?: string; message?: string };
}

interface XhrRequest {
  method: 'POST' | 'PUT';
  url: string;
  body: XMLHttpRequestBodyInit;
  headers?: Record<string, string>;
  withCredentials: boolean;
}

/** Cancelled by the person, or by the screen they left. */
function cancelled(): ApiError {
  return new ApiError(ApiErrorCode.UPLOAD_CANCELLED, 'Upload cancelled', 0);
}

function networkError(): ApiError {
  return new ApiError(ApiErrorCode.NETWORK_ERROR, 'No connection to the server', 0);
}

/**
 * Sends a body and reports how much of it has gone out.
 *
 * Resolves with the raw response text, because the two callers read it differently:
 * ours is an envelope and the storage's is nothing at all.
 */
function send(request: XhrRequest, options: UploadOptions): Promise<{ status: number; text: string }> {
  return new Promise((resolve, reject) => {
    if (options.signal?.aborted) {
      reject(cancelled());
      return;
    }

    const xhr = new XMLHttpRequest();

    xhr.open(request.method, request.url);
    xhr.withCredentials = request.withCredentials;

    for (const [name, value] of Object.entries(request.headers ?? {})) {
      xhr.setRequestHeader(name, value);
    }

    // `lengthComputable` is false for a body of unknown size, and reporting 0 % of 0
    // as NaN is how a progress bar ends up showing «NaN %».
    xhr.upload.addEventListener('progress', (event) => {
      options.onProgress?.({
        loaded: event.loaded,
        total: event.total,
        percent: event.lengthComputable && event.total > 0
          ? Math.round((event.loaded / event.total) * 100)
          : null,
      });
    });

    xhr.addEventListener('load', () => {
      resolve({ status: xhr.status, text: xhr.responseText });
    });

    // A failed upload and an aborted one arrive as different events, and only the
    // person who pressed cancel already knows what happened.
    xhr.addEventListener('error', () => reject(networkError()));
    xhr.addEventListener('timeout', () => reject(networkError()));
    xhr.addEventListener('abort', () => reject(cancelled()));

    const abort = (): void => xhr.abort();
    options.signal?.addEventListener('abort', abort, { once: true });
    xhr.addEventListener('loadend', () => options.signal?.removeEventListener('abort', abort));

    xhr.send(request.body);
  });
}

function parseError(status: number, text: string): ApiError {
  let body: ErrorEnvelope = {};

  try {
    body = JSON.parse(text) as ErrorEnvelope;
  } catch {
    // The storage answers XML and a proxy answers HTML. Neither must break in here.
  }

  return new ApiError(
    body.error?.code ?? ApiErrorCode.INTERNAL_ERROR,
    body.error?.message ?? `Error ${status}`,
    status,
    body.traceId,
  );
}

/**
 * Uploads to our own API as multipart, with the session cookie.
 *
 * There is no 401 refresh-and-retry here, unlike every other request: retrying means
 * sending the whole file again, and a body that has already been streamed cannot be
 * replayed reliably. A session that expires mid-upload surfaces as the error it is
 * and the person presses the button again — losing one upload is better than a
 * silent second one.
 */
export async function uploadToApi<T>(
  path: string,
  body: FormData,
  options: UploadOptions = {},
): Promise<T> {
  // No `content-type` header on purpose: the browser has to set it, because only it
  // knows the multipart boundary it generated.
  const { status, text } = await send(
    { method: 'POST', url: apiUrl(path), body, withCredentials: true },
    options,
  );

  if (status < 200 || status >= 300) throw parseError(status, text);
  if (status === 204 || !text) return undefined as T;

  return (JSON.parse(text) as Envelope<T>).data;
}

/**
 * Uploads straight to the storage with a pre-signed URL.
 *
 * `withCredentials` is false and stays false. The URL points at a host that is not
 * ours, the signature is the whole authorisation, and sending the session cookie
 * there would be handing the session to a third party for no reason.
 *
 * The content type has to match the one the URL was signed for, or the storage
 * rejects the upload with a signature error that says nothing about content types.
 */
export async function uploadToStorage(
  signedUrl: string,
  file: Blob,
  contentType: string,
  options: UploadOptions = {},
): Promise<void> {
  const { status, text } = await send(
    {
      method: 'PUT',
      url: signedUrl,
      body: file,
      headers: { 'content-type': contentType },
      withCredentials: false,
    },
    options,
  );

  if (status < 200 || status >= 300) throw parseError(status, text);
}
